/**
 * Calculation State Slice
 *
 * Manages cached calculation results including BOM, power calculations,
 * network topology, and other derived data.
 */

import type { StateCreator } from 'zustand';
import type { BillOfMaterials } from '../types/bom';

export interface CalculationSlice {
  // State
  bom: BillOfMaterials | null;
  isCalculating: boolean;
  lastCalculated: Date | null;
  calculationError: string | null;

  // Cached intermediate results
  computedNodes: number;
  computedRacks: number;
  computedPowerKw: number;
  networkSwitchCount: number;
  totalCables: number;

  // Actions
  setBOM: (bom: BillOfMaterials) => void;
  setIsCalculating: (isCalculating: boolean) => void;
  setCalculationError: (error: string | null) => void;
  clearCalculation: () => void;

  // Update intermediate results
  updateComputedValues: (values: {
    nodes?: number;
    racks?: number;
    powerKw?: number;
    switchCount?: number;
    cables?: number;
  }) => void;

  // Helpers
  getBOMSection: (sectionId: string) => BillOfMaterials['sections'][number] | undefined;
  getTotalCost: () => number;
  getTotalPowerKw: () => number;
  getTotalRacks: () => number;
}

export const createCalculationSlice: StateCreator<
  CalculationSlice,
  [],
  [],
  CalculationSlice
> = (set, get) => ({
  // Initial state
  bom: null,
  isCalculating: false,
  lastCalculated: null,
  calculationError: null,

  computedNodes: 0,
  computedRacks: 0,
  computedPowerKw: 0,
  networkSwitchCount: 0,
  totalCables: 0,

  // Actions
  setBOM: (bom) =>
    set({
      bom,
      lastCalculated: new Date(),
      calculationError: null,
    }),

  setIsCalculating: (isCalculating) =>
    set({ isCalculating }),

  setCalculationError: (error) =>
    set({
      calculationError: error,
      isCalculating: false,
    }),

  clearCalculation: () =>
    set({
      bom: null,
      lastCalculated: null,
      calculationError: null,
      computedNodes: 0,
      computedRacks: 0,
      computedPowerKw: 0,
      networkSwitchCount: 0,
      totalCables: 0,
    }),

  updateComputedValues: (values) =>
    set((state) => ({
      computedNodes: values.nodes ?? state.computedNodes,
      computedRacks: values.racks ?? state.computedRacks,
      computedPowerKw: values.powerKw ?? state.computedPowerKw,
      networkSwitchCount: values.switchCount ?? state.networkSwitchCount,
      totalCables: values.cables ?? state.totalCables,
    })),

  // Helpers
  getBOMSection: (sectionId) => {
    const bom = get().bom;
    if (!bom) return undefined;
    return bom.sections.find((s) => s.id === sectionId);
  },

  getTotalCost: () => {
    const bom = get().bom;
    if (!bom?.summary.cost) return 0;
    return bom.summary.cost.totalCapex;
  },

  getTotalPowerKw: () => {
    const bom = get().bom;
    if (!bom) return 0;
    return bom.summary.power.totalFacilityPowerKw;
  },

  getTotalRacks: () => {
    const bom = get().bom;
    if (!bom) return 0;
    return bom.summary.space.totalRacks;
  },
});
